/**
 * Resolve as fotos de cada serviço a partir do manifesto `src/data/serviceGallery.js`,
 * com fallback para o `service_image` do catálogo (`src/data/services.js`).
 *
 * Cada item devolvido é um de dois tipos:
 *   { type: 'photo',   src, alt }             → uma foto normal
 *   { type: 'compare', before, after, alt }   → um par antes/depois
 */

import { serviceGallery, MAX_IMAGES_PER_SERVICE } from '../data/serviceGallery'

/** Normaliza um caminho do manifesto para um URL servido a partir de `public/`. */
const toUrl = (path) => {
  if (!path) return null
  if (/^(https?:)?\/\//.test(path) || path.startsWith('/')) return path
  return `/${path}`
}

const isPair = (item) =>
  item !== null && typeof item === 'object' && (item.antes || item.depois)

/**
 * Lista de fotos de um serviço, já limitada a MAX_IMAGES_PER_SERVICE.
 * @param {object} service serviço do catálogo (precisa de `service_id` e `name`)
 * @returns {Array<{type: string, src?: string, before?: string, after?: string, alt: string}>}
 */
export const getServiceMedia = (service) => {
  if (!service) return []

  const entries = serviceGallery[service.service_id]
  const items =
    Array.isArray(entries) && entries.length
      ? entries
      : service.service_image
      ? [service.service_image]
      : []

  return items
    .slice(0, MAX_IMAGES_PER_SERVICE)
    .map((item, index) => {
      if (isPair(item)) {
        const before = toUrl(item.antes)
        const after = toUrl(item.depois)
        // Um par incompleto degrada para foto simples em vez de partir a galeria.
        if (!before || !after) {
          const only = before || after
          return only ? { type: 'photo', src: only, alt: service.name } : null
        }
        return {
          type: 'compare',
          before,
          after,
          alt: `${service.name} — antes e depois`,
        }
      }

      const src = toUrl(item)
      if (!src) return null
      return {
        type: 'photo',
        src,
        alt: `${service.name} — foto ${index + 1}`,
      }
    })
    .filter(Boolean)
}

/**
 * Uma única imagem representativa do serviço (Destaques, miniaturas, partilhas).
 * Num par antes/depois usa-se o "depois", que é o resultado final.
 */
export const getServiceCover = (service) => {
  const [first] = getServiceMedia(service)
  if (!first) return null
  return first.type === 'compare' ? first.after : first.src
}

/** Quantas fotos existem para o serviço (um par antes/depois conta como uma). */
export const getServiceMediaCount = (service) => getServiceMedia(service).length

export default getServiceMedia
